import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../../utils/api';
import RichTextEditor from '../RichTextEditor';
import EmployeeMultiSelect from '../EmployeeMultiSelect';
import RecentOptions from '../../components/RecentOptions';
import { recordRecentPick } from '../../utils/recentPicks';
import Avatar from '../Avatar';
import {
    EMAIL, browserTimezone, fromInputValue, toInputValue
} from './meetingHelpers';
import '../../styles/meetings.css';

const MANAGEMENT = ['HR', 'ADMIN', 'MANAGER', 'ACCOUNTS', 'TEAM LEAD'];

const nextHalfHour = () => {
    const d = new Date();
    d.setSeconds(0, 0);
    d.setMinutes(d.getMinutes() < 30 ? 30 : 60);
    return d;
};

/** The form's starting values: blank for a new entry, or read back from `meeting` when editing. */
const initialForm = (meeting, kind) => {
    if (!meeting) {
        const start = nextHalfHour();
        const end = new Date(start.getTime() + 60 * 60 * 1000);
        return {
            kind,
            title: '',
            description: '',
            location: '',
            allDay: false,
            start: toInputValue(start, false),
            end: toInputValue(end, false),
            visibility: 'invited',
            projectId: '',
            userIds: [],
            coHostIds: [],
            externalUserIds: [],
            guests: [],
            lobby: true,
            guestsCanScreenShare: true,
            guestsCanJoinBeforeHost: false
        };
    }

    const others = (meeting.participants || []).filter(p => p.role !== 'organizer');
    return {
        kind: meeting.kind,
        title: meeting.title,
        description: meeting.description || '',
        location: meeting.location || '',
        allDay: Boolean(meeting.allDay),
        start: toInputValue(meeting.startsAt, meeting.allDay),
        end: toInputValue(meeting.endsAt, meeting.allDay),
        visibility: meeting.visibility,
        projectId: meeting.projectId || '',
        userIds: others.filter(p => p.user).map(p => p.user._id || p.user),
        coHostIds: others.filter(p => p.role === 'cohost' && p.user).map(p => p.user._id || p.user),
        externalUserIds: others.filter(p => p.externalUser).map(p => String(p.externalUser)),
        guests: others.filter(p => p.isExternal && !p.externalUser).map(p => ({ email: p.email, name: p.name })),
        lobby: meeting.settings?.lobby ?? true,
        guestsCanScreenShare: meeting.settings?.guestsCanScreenShare ?? true,
        guestsCanJoinBeforeHost: meeting.settings?.guestsCanJoinBeforeHost ?? false
    };
};

/**
 * Create or edit a meeting or an event.
 *
 * `meeting` set means edit. Times are entered in the browser's own timezone and
 * stored as exact instants; the timezone name travels with the entry so emails
 * (Step 8) can word it for people elsewhere.
 */
const MeetingFormModal = ({ meeting, kind = 'meeting', currentUser, onClose, onSaved }) => {
    const editing = Boolean(meeting);
    const [form, setForm] = useState(() => initialForm(meeting, kind));
    const [employees, setEmployees] = useState([]);
    const [externals, setExternals] = useState([]);
    const [canPickExternals, setCanPickExternals] = useState(true);
    const [projects, setProjects] = useState([]);
    const [guestInput, setGuestInput] = useState('');
    const [saving, setSaving] = useState(false);

    const isMeeting = form.kind === 'meeting';
    const isManagement = MANAGEMENT.includes(currentUser?.role);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        let alive = true;
        api.get('/employees/directory')
            .then(res => { if (alive) setEmployees(res.data || []); })
            .catch(() => { });
        // The external directory is open to some roles only. Anyone else can
        // still invite a guest by typing an email.
        api.get('/external-users')
            .then(res => { if (alive) setExternals(res.data || []); })
            .catch(() => { if (alive) setCanPickExternals(false); });
        // The projects this person may tie the meeting to (their own, or all for admin and HR).
        api.get('/meetings/options/projects')
            .then(res => { if (alive) setProjects(res.data || []); })
            .catch(() => { });
        return () => { alive = false; };
    }, []);

    const set = (patch) => setForm(f => ({ ...f, ...patch }));

    const people = useMemo(
        () => employees.filter(e => String(e._id) !== String(currentUser?.id || currentUser?._id)),
        [employees, currentUser]
    );

    // The external picker reuses the employee picker, which filters on
    // name / employeeId / role - so a contact's company and type go there.
    const externalOptions = useMemo(
        () => externals.map(x => ({ _id: x._id, name: x.name, employeeId: x.company || x.email, role: x.type })),
        [externals]
    );

    const pickedEmployees = people.filter(p => form.userIds.includes(p._id));

    const onInvite = (ids) => set({ userIds: ids, coHostIds: form.coHostIds.filter(id => ids.includes(id)) });

    const toggleCoHost = (id) => set({
        coHostIds: form.coHostIds.includes(id) ? form.coHostIds.filter(x => x !== id) : [...form.coHostIds, id]
    });

    const addGuests = (raw) => {
        const parts = raw.split(/[,;\s]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
        if (!parts.length) return;
        const bad = parts.filter(p => !EMAIL.test(p));
        if (bad.length) {
            Swal.fire('Check the email', `"${bad[0]}" is not a valid email address.`, 'warning');
            return;
        }
        const known = new Set(form.guests.map(g => g.email));
        const fresh = parts.filter((p, i) => !known.has(p) && parts.indexOf(p) === i).map(email => ({ email, name: '' }));
        set({ guests: [...form.guests, ...fresh] });
        setGuestInput('');
    };

    const switchAllDay = (allDay) => {
        // Keep the same day when flipping between a time and a whole day.
        const start = fromInputValue(form.start, form.allDay);
        const end = fromInputValue(form.end, form.allDay, { endOfDay: true });
        set({
            allDay,
            start: start ? toInputValue(start, allDay) : form.start,
            end: end ? toInputValue(end, allDay) : form.end
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;

        const startsAt = fromInputValue(form.start, form.allDay);
        const endsAt = fromInputValue(form.end, form.allDay, { endOfDay: true });
        if (!form.title.trim()) return Swal.fire('Title needed', 'Give it a title.', 'warning');
        if (!startsAt || !endsAt) return Swal.fire('Check the times', 'Choose when it starts and ends.', 'warning');
        if (new Date(endsAt) <= new Date(startsAt) && !form.allDay) {
            return Swal.fire('Check the times', 'It must end after it starts.', 'warning');
        }

        const body = {
            kind: form.kind,
            title: form.title.trim(),
            description: form.description,
            location: form.location,
            allDay: form.allDay,
            startsAt,
            endsAt,
            timezone: browserTimezone(),
            visibility: form.visibility,
            projectId: form.projectId || null,
            userIds: form.userIds,
            coHostIds: form.coHostIds,
            externalUserIds: form.externalUserIds,
            guests: form.guests,
            settings: {
                lobby: form.lobby,
                guestsCanScreenShare: form.guestsCanScreenShare,
                guestsCanJoinBeforeHost: form.guestsCanJoinBeforeHost
            }
        };

        setSaving(true);
        try {
            const res = editing
                ? await api.put(`/meetings/${meeting._id}`, body)
                : await api.post('/meetings', body);
            onSaved(res.data);
        } catch (err) {
            Swal.fire('Could not save', err.response?.data?.message || 'Something went wrong. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const noun = isMeeting ? 'meeting' : 'event';

    return (
        <div className="mt-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <form className="mt-modal" onSubmit={submit} role="dialog" aria-modal="true" aria-label={`${editing ? 'Edit' : 'New'} ${noun}`}>
                <div className="mt-modal-head">
                    <h3>{editing ? `Edit ${noun}` : `New ${noun}`}</h3>
                    <button type="button" className="mt-icon-btn" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="mt-modal-body">
                    {!editing && (
                        <div className="mt-kind-switch" role="tablist">
                            <button type="button" className={isMeeting ? 'on' : ''} onClick={() => set({ kind: 'meeting' })}>
                                Meeting <small>with a video room</small>
                            </button>
                            <button type="button" className={!isMeeting ? 'on' : ''} onClick={() => set({ kind: 'event' })}>
                                Event <small>calendar only</small>
                            </button>
                        </div>
                    )}

                    <label className="mt-field">
                        <span>Title</span>
                        <input
                            type="text"
                            value={form.title}
                            maxLength={200}
                            autoFocus
                            placeholder={isMeeting ? 'Weekly project review' : 'Company offsite'}
                            onChange={(e) => set({ title: e.target.value })}
                        />
                    </label>

                    <label className="mt-check">
                        <input type="checkbox" checked={form.allDay} onChange={(e) => switchAllDay(e.target.checked)} />
                        All day
                    </label>

                    <div className="mt-row">
                        <label className="mt-field">
                            <span>Starts</span>
                            <input
                                type={form.allDay ? 'date' : 'datetime-local'}
                                value={form.start}
                                onChange={(e) => set({ start: e.target.value })}
                            />
                        </label>
                        <label className="mt-field">
                            <span>Ends</span>
                            <input
                                type={form.allDay ? 'date' : 'datetime-local'}
                                value={form.end}
                                min={form.start}
                                onChange={(e) => set({ end: e.target.value })}
                            />
                        </label>
                    </div>

                    {!isMeeting && (
                        <label className="mt-field">
                            <span>Location</span>
                            <input
                                type="text"
                                value={form.location}
                                maxLength={300}
                                placeholder="Where is it?"
                                onChange={(e) => set({ location: e.target.value })}
                            />
                        </label>
                    )}

                    <div className="mt-field">
                        <span>{isMeeting ? 'Agenda' : 'Details'}</span>
                        <RichTextEditor
                            rows={3}
                            value={form.description}
                            placeholder="Optional"
                            ariaLabel={isMeeting ? 'Agenda' : 'Details'}
                            onChange={(v) => set({ description: v })}
                        />
                    </div>

                    {/* A project already chosen stays selectable even if it has since left the list. */}
                    {(projects.length > 0 || form.projectId) && (
                        <label className="mt-field">
                            <span>Project (optional)</span>
                            <select value={form.projectId} onChange={(e) => { set({ projectId: e.target.value }); recordRecentPick('project', e.target.value); }}>
                                <option value="">Not linked to a project</option>
                                {form.projectId && !projects.some(p => p._id === form.projectId) && (
                                    <option value={form.projectId}>{meeting?.project?.name || 'Current project'}</option>
                                )}
                                <RecentOptions kind="project" items={projects} />
                            </select>
                        </label>
                    )}

                    <div className="mt-field">
                        <span>Invite employees</span>
                        <EmployeeMultiSelect
                            employees={people}
                            selected={form.userIds}
                            onChange={onInvite}
                            recentKey="meeting-guest"
                            placeholder="Search and add people..."
                            emptyText="No employees to invite."
                        />
                    </div>

                    {pickedEmployees.length > 0 && (
                        <div className="mt-cohosts">
                            <span className="mt-subtle">Co-hosts can edit and run the {noun}.</span>
                            {pickedEmployees.map(p => (
                                <label key={p._id} className="mt-cohost">
                                    <input
                                        type="checkbox"
                                        checked={form.coHostIds.includes(p._id)}
                                        onChange={() => toggleCoHost(p._id)}
                                    />
                                    <Avatar name={p.name} profilePic={p.profilePic} className="mt-mini-avatar" />
                                    {p.name}
                                </label>
                            ))}
                        </div>
                    )}

                    {canPickExternals && externalOptions.length > 0 && (
                        <div className="mt-field">
                            <span>Invite from Guests</span>
                            <EmployeeMultiSelect
                                employees={externalOptions}
                                selected={form.externalUserIds}
                                onChange={(ids) => set({ externalUserIds: ids })}
                                recentKey="meeting-guest"
                                placeholder="Search vendors, clients, consultants..."
                                emptyText="No guests yet."
                            />
                        </div>
                    )}

                    <div className="mt-field">
                        <span>Invite other people by email</span>
                        <div className="mt-guest-entry">
                            <input
                                type="text"
                                value={guestInput}
                                placeholder="name@company.com, then Enter"
                                onChange={(e) => setGuestInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addGuests(guestInput); }
                                }}
                                onBlur={() => guestInput.trim() && addGuests(guestInput)}
                            />
                        </div>
                        {form.guests.length > 0 && (
                            <div className="mt-chips">
                                {form.guests.map(g => (
                                    <span key={g.email} className="mt-chip">
                                        {g.name || g.email}
                                        <button
                                            type="button"
                                            aria-label={`Remove ${g.email}`}
                                            onClick={() => set({ guests: form.guests.filter(x => x.email !== g.email) })}
                                        >
                                            ✕
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <small className="mt-subtle">They get an email with a link to join. No account needed.</small>
                    </div>

                    {isMeeting && (
                        <fieldset className="mt-settings">
                            <legend>Guest settings</legend>
                            <label className="mt-check">
                                <input type="checkbox" checked={form.lobby} onChange={(e) => set({ lobby: e.target.checked })} />
                                Hold uninvited people in a waiting room
                            </label>
                            <label className="mt-check">
                                <input
                                    type="checkbox"
                                    checked={form.guestsCanScreenShare}
                                    onChange={(e) => set({ guestsCanScreenShare: e.target.checked })}
                                />
                                Guests can share their screen
                            </label>
                            <label className="mt-check">
                                <input
                                    type="checkbox"
                                    checked={form.guestsCanJoinBeforeHost}
                                    onChange={(e) => set({ guestsCanJoinBeforeHost: e.target.checked })}
                                />
                                Guests can join before the host
                            </label>
                        </fieldset>
                    )}

                    {isManagement && (
                        <label className="mt-check">
                            <input
                                type="checkbox"
                                checked={form.visibility === 'company'}
                                onChange={(e) => set({ visibility: e.target.checked ? 'company' : 'invited' })}
                            />
                            Show on everyone's calendar (whole company)
                        </label>
                    )}
                </div>

                <div className="mt-modal-foot">
                    <button type="button" className="mt-btn ghost" onClick={onClose}>Cancel</button>
                    <button type="submit" className="mt-btn primary" disabled={saving}>
                        {saving && <FontAwesomeIcon icon={faSpinner} spin />}
                        {editing ? 'Save changes' : `Create ${noun}`}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default MeetingFormModal;
